import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import { apiCall, API_URL } from '../utils/api';

const AdminClassesManagement = () => {
    const [classes, setClasses] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingClass, setEditingClass] = useState(null);

    const [classForm, setClassForm] = useState({
        title: '',
        description: '',
        duration: '',
        level: 'Beginner',
        fees: '',
        imageUrl: '',
        enabled: true,
        displayOrder: 0
    });

    const levels = ['Beginner', 'Intermediate', 'Advanced'];

    // Load classes from API
    useEffect(() => {
        fetchClasses();
    }, []);

    const fetchClasses = async () => {
        try {
            const data = await apiCall('/classes');
            // Transform data to match component state
            const mapped = data.map(c => ({
                id: c._id, // Ensure to use _id
                title: c.title,
                description: c.description,
                duration: c.duration,
                level: c.level,
                fees: c.price,
                // Fix image URL mapping for Cloudinary
                imageUrl: c.image ? (c.image.startsWith('http') ? c.image : `${API_URL.replace('/api', '')}/${c.image}`) : '',
                enabled: c.status === 'active',
                displayOrder: 0 // DB doesn't have displayOrder yet
            }));
            setClasses(mapped);
        } catch (error) {
            console.error(error);
        }
    };

    const handleAddClass = () => {
        setClassForm({
            title: '',
            description: '',
            duration: '',
            level: 'Beginner',
            fees: '',
            imageUrl: '',
            imageFile: null,
            enabled: true,
            displayOrder: classes.length + 1
        });
        setEditingClass(null);
        setShowForm(true);
    };

    const handleEditClass = (classItem) => {
        setClassForm({
            ...classItem,
            imageFile: null // Reset file on edit
        });
        setEditingClass(classItem.id);
        setShowForm(true);
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setClassForm({ ...classForm, imageUrl: reader.result, imageFile: file });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveClass = async () => {
        if (!classForm.title || !classForm.description || !classForm.duration || !classForm.fees) {
            alert('Please fill all required fields');
            return;
        }

        const formData = new FormData();
        formData.append('title', classForm.title);
        formData.append('description', classForm.description);
        formData.append('duration', classForm.duration);
        formData.append('level', classForm.level);
        formData.append('price', parseFloat(classForm.fees) || 0);

        // Status
        formData.append('status', classForm.enabled ? 'active' : 'inactive');

        if (classForm.imageFile) {
            formData.append('image', classForm.imageFile);
        }

        try {
            if (editingClass) {
                await apiCall(`/classes/${editingClass}`, 'PUT', formData, true);
                alert('Class updated successfully!');
            } else {
                await apiCall('/classes', 'POST', formData, true);
                alert('Class created successfully!');
            }
            fetchClasses();
            setShowForm(false);
        } catch (error) {
            alert('Failed to save class: ' + error.message);
        }
    };

    const handleDeleteClass = async (id) => {
        if (window.confirm('Are you sure you want to delete this class?')) {
            try {
                await apiCall(`/classes/${id}`, 'DELETE');
                alert('Class deleted successfully!');
                fetchClasses();
            } catch (error) {
                alert('Failed to delete: ' + error.message);
            }
        }
    };

    const toggleClassStatus = async (id) => {
        const cls = classes.find(c => c.id === id);
        const newStatus = cls.enabled ? 'inactive' : 'active';

        const formData = new FormData();
        formData.append('status', newStatus);

        try {
            await apiCall(`/classes/${id}`, 'PUT', formData, true); // true for isFileUpload ensures no JSON content-type
            // Update local state immediately for better UX
            setClasses(classes.map(c => c.id === id ? { ...c, enabled: !c.enabled } : c));
        } catch (error) {
            alert('Failed to update status: ' + error.message);
        }
    };

    return (
        <div className="admin-classes-management">
            <div className="section-header">
                <h2>🧘 Classes Management</h2>
                <p>Manage yoga classes displayed on the Classes page</p>
            </div>

            <div className="card">
                <div className="card-header">
                    <h3>Yoga Classes</h3>
                    <button onClick={handleAddClass} className="btn btn-primary">
                        ➕ Add Class
                    </button>
                </div>
                <div className="card-content">
                    {showForm && (
                        <div className="form-card" style={{ marginBottom: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                            <h4>{editingClass ? 'Edit Class' : 'Add New Class'}</h4>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Class Title *</label>
                                    <input
                                        type="text"
                                        value={classForm.title}
                                        onChange={(e) => setClassForm({ ...classForm, title: e.target.value })}
                                        placeholder="e.g., Beginner Yoga"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Duration *</label>
                                    <input
                                        type="text"
                                        value={classForm.duration}
                                        onChange={(e) => setClassForm({ ...classForm, duration: e.target.value })}
                                        placeholder="e.g., 60 minutes"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Level *</label>
                                    <select
                                        value={classForm.level}
                                        onChange={(e) => setClassForm({ ...classForm, level: e.target.value })}
                                    >
                                        {levels.map(level => (
                                            <option key={level} value={level}>{level}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Fees *</label>
                                    <input
                                        type="text"
                                        value={classForm.fees}
                                        onChange={(e) => setClassForm({ ...classForm, fees: e.target.value })}
                                        placeholder="e.g., ₹500/month"
                                    />
                                </div>

                                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                    <label>Description *</label>
                                    <textarea
                                        value={classForm.description}
                                        onChange={(e) => setClassForm({ ...classForm, description: e.target.value })}
                                        placeholder="Enter class description"
                                        rows="3"
                                        style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Display Order</label>
                                    <input
                                        type="number"
                                        value={classForm.displayOrder}
                                        onChange={(e) => setClassForm({ ...classForm, displayOrder: parseInt(e.target.value) })}
                                    />
                                </div>

                                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                    <label>Upload Class Image</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                    />
                                    <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                                        Recommended size: 400x300px (4:3 ratio) for uniform card display
                                    </small>
                                    {classForm.imageUrl && (
                                        <div style={{ marginTop: '10px' }}>
                                            <img
                                                src={classForm.imageUrl}
                                                alt="Preview"
                                                style={{
                                                    width: '300px',
                                                    height: '225px',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px',
                                                    border: '2px solid #ddd'
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={classForm.enabled}
                                            onChange={(e) => setClassForm({ ...classForm, enabled: e.target.checked })}
                                        />
                                        {' '}Visible on Frontend
                                    </label>
                                </div>
                            </div>

                            <div className="form-actions" style={{ marginTop: '20px' }}>
                                <button onClick={handleSaveClass} className="btn btn-success">💾 Save Class</button>
                                <button onClick={() => setShowForm(false)} className="btn btn-secondary">Cancel</button>
                            </div>
                        </div>
                    )}

                    {/* Classes Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px', marginTop: '20px' }}>
                        {classes.sort((a, b) => a.displayOrder - b.displayOrder).map(classItem => (
                            <div
                                key={classItem.id}
                                className="class-item-card"
                                style={{
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    background: '#fff',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                {/* Class Image */}
                                <div style={{ position: 'relative' }}>
                                    {classItem.imageUrl ? (
                                        <img
                                            src={classItem.imageUrl}
                                            alt={classItem.title}
                                            style={{
                                                width: '100%',
                                                height: '200px',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    ) : (
                                        <div style={{
                                            width: '100%',
                                            height: '200px',
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontSize: '48px'
                                        }}>
                                            🧘
                                        </div>
                                    )}
                                    <div style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        background: classItem.enabled ? '#28a745' : '#dc3545',
                                        color: 'white',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 'bold'
                                    }}>
                                        {classItem.enabled ? 'ACTIVE' : 'DISABLED'}
                                    </div>
                                    <div style={{
                                        position: 'absolute',
                                        top: '10px',
                                        left: '10px',
                                        background: 'rgba(0,0,0,0.7)',
                                        color: 'white',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '12px'
                                    }}>
                                        {classItem.level}
                                    </div>
                                </div>

                                {/* Class Details */}
                                <div style={{ padding: '20px' }}>
                                    <h3 style={{ margin: '0 0 10px 0', fontSize: '20px', color: '#333' }}>
                                        {classItem.title}
                                    </h3>
                                    <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                                        {classItem.description}
                                    </p>

                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '10px',
                                        marginBottom: '15px',
                                        padding: '15px',
                                        background: '#f8f9fa',
                                        borderRadius: '6px'
                                    }}>
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Duration</div>
                                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#333' }}>{classItem.duration}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Fees</div>
                                            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#4CAF50' }}>{classItem.fees}</div>
                                        </div>
                                    </div>

                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '15px' }}>
                                        Display Order: {classItem.displayOrder}
                                    </div>

                                    {/* Action Buttons */}
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                        <button
                                            onClick={() => handleEditClass(classItem)}
                                            className="btn btn-sm btn-primary"
                                            style={{ flex: 1 }}
                                        >
                                            ✏️ Edit
                                        </button>
                                        <button
                                            onClick={() => toggleClassStatus(classItem.id)}
                                            className={`btn btn-sm ${classItem.enabled ? 'btn-warning' : 'btn-success'}`}
                                            style={{ flex: 1 }}
                                        >
                                            {classItem.enabled ? '🔴 Disable' : '🟢 Enable'}
                                        </button>
                                        <button
                                            onClick={() => handleDeleteClass(classItem.id)}
                                            className="btn btn-sm btn-danger"
                                            style={{ flex: 1 }}
                                        >
                                            🗑️ Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {classes.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                            <p>No classes found. Click "Add Class" to create your first class.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Quick Stats */}
            <div className="card" style={{ marginTop: '30px' }}>
                <div className="card-header">
                    <h3>Classes Overview</h3>
                </div>
                <div className="card-content">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                        <div style={{ padding: '20px', background: '#e3f2fd', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1976d2' }}>{classes.length}</div>
                            <div style={{ fontSize: '14px', color: '#666' }}>Total Classes</div>
                        </div>
                        <div style={{ padding: '20px', background: '#e8f5e9', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#388e3c' }}>
                                {classes.filter(c => c.enabled).length}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>Active Classes</div>
                        </div>
                        <div style={{ padding: '20px', background: '#fff3e0', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f57c00' }}>
                                {classes.filter(c => c.level === 'Beginner').length}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>Beginner</div>
                        </div>
                        <div style={{ padding: '20px', background: '#fce4ec', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#c2185b' }}>
                                {classes.filter(c => c.level === 'Intermediate').length}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>Intermediate</div>
                        </div>
                        <div style={{ padding: '20px', background: '#f3e5f5', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#7b1fa2' }}>
                                {classes.filter(c => c.level === 'Advanced').length}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>Advanced</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminClassesManagement;
